import { useContext, createContext, useState, useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import {
    clearSearchFilters,
    setSearchFiltersV2,
    setSearchStatusType,
    clearSearchResultsPropertySummaries,
} from 'js/actions/clapi';
import OTM from 'js/constants/otm';
import RapidSearchConstant, { INITIAL_ERROR_FIELDS } from 'js/constants/rapidSearch';
import { trackEvent, trackApplyFilter } from 'js/actions/segment';
import Segment from 'js/helpers/Segment';
import MixPanelHelper from 'js/helpers/mixpanel/MixPanelHelper';
import SegmentConstants from 'js/constants/segment';
import saveAndGetUserSettings from 'js/helpers/userPreferences/saveAndGetUserSetting';
import {
    LOCALIZED_ACTIVE_LISTING_ONLY_SETTING,
    LOCALIZED_RADIUS_SORT_BY_SETTING,
    LOCALIZED_SORT_BY_SETTING,
} from 'js/helpers/Localization';
import listPropertyPreview from 'js/constants/listPropertyPreview';
import { resetNotifications, setShouldRefresh } from 'js/actions/savedSearch';
import useFilters from 'js/hooks/useFilters';
import { getRapidSavedList } from 'js/actions/watchList';
import { fetchAggregateFilters } from 'js/actions/aggregateFilters';
import { extractNonBaseFilters } from 'js/helpers/search/AggregateSearchHelper';
import usePruneSavedSearchFilters from 'js/hooks/usePruneSavedSearchFilters';
import useActiveView from '../../hooks/useActiveView';
import useSearchUrlGenerator from '../../hooks/multiLocality/useSearchUrlGenerator';
import useRefineSearchEventTracker from 'js/hooks/useRefineSearchEventTracker';
import { clearDrawings } from 'js/actions/map';
import ListPropertyPreview from '../../constants/listPropertyPreview';

export const FiltersFormContext = createContext({});
export const useFiltersFormContext = () => useContext(FiltersFormContext);

const { EVENT_PROPERTIES } = SegmentConstants;
const { SEARCH } = EVENT_PROPERTIES;
export const WITH_USER_SETTINGS = [{ field: 'isActiveListingsOnly', name: LOCALIZED_ACTIVE_LISTING_ONLY_SETTING }];

export const FiltersFormContextProvider = (props) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const statusType = useSelector(state => state.searchResults.get('statusType') ?? props.statusType);
    const radiusSearchTargetProperty = useSelector(store => store.rapid.get('targetProperty').value, shallowEqual);
    const aggregateFilterOptions = useSelector(state => state.aggregateFilters?.options);
    const [errorFields, setErrorFields] = useState(INITIAL_ERROR_FIELDS);
    const [initialized, setInitialized] = useState(false);
    const {
        statusType: initialStatusType = null,
        ...filtersFromUrl
    } = props?.urlFilters || {};

    const filters = useFilters({
        urlStatusType: initialStatusType,
        statusType,
        searchType: props.searchType,
        filters: props.filters,
        baseFilters: props.baseFilters,
        sort: props.sort,
        isSavedSearch: props.isSavedSearch,
        isRadiusSearch: props.isRadiusSearch,
        filtersFromUrl,
        savedList: props.savedList,
    });

    usePruneSavedSearchFilters({
        filters,
        aggregateFilterOptions,
        savedSearchFilters: props.filters,
        isSavedSearch: props.isSavedSearch,
        statusType,
    });

    const updateUrl = useSearchUrlGenerator({
        searchType: props.searchType,
        filters,
        shouldUpdate: props.withPermaLinks,
        filtersFromUrl,
    });

    // card or list
    const { activeView } = useActiveView();

    const getProperties = _filters => {
        if (props.savedList) {
            dispatch(getRapidSavedList({
                filters: _filters,
                params: {
                    size: props.savedList.limit,
                },
                type: !!props.savedList.id ? 'saved' : 'watch',
                savedListId: props.savedList.id,
            }));
        } else {
            dispatch(clearSearchResultsPropertySummaries());
            dispatch(setSearchFiltersV2(_filters))
        }
    };

    const resetToDefault = (_statusType = statusType, skipGetProperties = false, isInit = false) => {
        // include map-related search results via drawing if there's any
        const defaultFiltersWithMapSearch = filters.includeMapSearch(filters.value, filters.defaultValue, isInit);
        const flatFilters = filters.getFlatFilters(defaultFiltersWithMapSearch[_statusType], _statusType);

        if (!skipGetProperties) {
            getProperties(flatFilters);
        }

        filters.reset(isInit);
        setErrorFields(INITIAL_ERROR_FIELDS);
        updateUrl({ flatFilters, statusType: _statusType });
    };

    const resetToDefaultMap = () => {
        resetToDefault(OTM.ALL, true, false);
        dispatch(setSearchStatusType(OTM.ALL));
    };

    const resetFilter = () => {
        resetToDefault();
        dispatch(trackEvent(Segment.resetFilters({
            statusType: RapidSearchConstant.TABS_ATTR[statusType],
            activeView,
        })));
    }

    const updateFilterUserSettings = (statusTypeParam) => {
        WITH_USER_SETTINGS.forEach(fieldUserSetting => {
            // if statusTypeParam is not empty, it means that it is passed from the initial load
            const statusTypeToUse = statusTypeParam || statusType;
            const shouldSave = !statusTypeParam
                ? filters.isModified(fieldUserSetting.field)
                : filtersFromUrl.hasOwnProperty('isActiveListingsOnly');
            if (Object.keys(filters.value[statusTypeToUse]).includes(fieldUserSetting.field) && shouldSave) {
                saveAndGetUserSettings(dispatch, {
                    name: fieldUserSetting.name[statusTypeToUse],
                    value: filters.value[statusTypeToUse].isActiveListingsOnly,
                });
            }
        });
    };

    const logFilters = (_filters, _statusType) => {
        const areaFilters = Object.entries(JSON.parse(JSON.stringify(filters.value[statusType])))
            .reduce((result, [key, value]) => {
                if (RapidSearchConstant.AREA_FILTERS.includes(key)) {
                    Object.defineProperty(result, key, {
                        value,
                        writable: true,
                        enumerable: true,
                        configurable: true
                    });
                }
                return result;
            }, {});

        if (_filters.mapSearch?.skipLogFilters) {
            // resets the flag for next use
            _filters.mapSearch.skipLogFilters = false;
            return;
        };

        dispatch(trackApplyFilter(MixPanelHelper
            .constructMixpanelProperties(_statusType, activeView, {
                ..._filters,
                ...areaFilters,
            })));
    };

    const applyFilter = (immediateFilters = {}, isInit = false, _statusType = statusType) => {
        const _filters = filters.getFlatFilters(immediateFilters, _statusType);

        getProperties(_filters);

        if (props.isRadiusSearch && filters.isModified('radius')) {
            dispatch(fetchAggregateFilters({ ...props.baseFilters, radius: _filters.radius }));
        }

        if ('mapSearch' in immediateFilters) {
            dispatch(fetchAggregateFilters(extractNonBaseFilters(_filters)));
        }
        filters.save(immediateFilters);
        updateFilterUserSettings();
        logFilters({
            ..._filters,
            suburb: props.isRadiusSearch ? (immediateFilters.suburbOnly ?? filters.value[_statusType].suburbOnly) : undefined,
        }, _statusType);
        updateUrl({
            statusType: _statusType,
            ...(props.isRadiusSearch ? { suburbOnly: filters.value[_statusType].suburbOnly } : {}),
            ..._filters,
        });
    };

    const applySort = (immediateFilters = {}, _statusType = statusType) => {
        const sortFilters = {
            ...filters.previousValue[_statusType],
            ...immediateFilters,
        };
        const flatFilters = filters.getFlatFilters(sortFilters);
        getProperties(flatFilters);
        updateUrl({ statusType: _statusType, ...flatFilters });
        filters.save(sortFilters);
        const SORT_BY_SETTING = props.isRadiusSearch
            ? LOCALIZED_RADIUS_SORT_BY_SETTING
            : LOCALIZED_SORT_BY_SETTING;
        saveAndGetUserSettings(dispatch, {
            name: SORT_BY_SETTING[_statusType],
            value: JSON.stringify(immediateFilters.sort),
        });
    };

    const handleOnChangeStatusType = (status) => {
        dispatch(setSearchStatusType(status));
        getProperties(filters.getFlatFilters(filters.previousValue[status], status));
        // set statusType into redux so higher level component can access
        // it was used to determine mixpanel properties and which feature
        // flag to use based on statusType
        if (props.isSavedSearch) {
            dispatch(resetNotifications());
            dispatch(setShouldRefresh(false));
        }
        dispatch(trackEvent(Segment.switchResultTab({
            page: SEARCH,
            searchType: listPropertyPreview.MIXPANEL_SEARCH_TYPE[props.searchType],
            searchTab: RapidSearchConstant.TABS_ATTR[status],
        })));
        updateUrl({ statusType: status, ...filters.getFlatFilters({}, status) });
    };

    const _setErrorFields = (isError, name) => {
        setErrorFields((prevState) => {
            const currentStateByStatusType = JSON.parse(JSON.stringify(prevState[statusType]));
            if (isError && !currentStateByStatusType.includes(name)) {
                currentStateByStatusType.push(name);
            } else if (!isError && currentStateByStatusType.includes(name)) {
                currentStateByStatusType.splice(currentStateByStatusType.indexOf(name), 1);
            }
            return { ...prevState, [statusType]: currentStateByStatusType };
        });
    };

    const isASearchType = () => {
        const urlParams = new URLSearchParams(location.search);
        return [
            'suggestions', // indicates an address search
            'radius', // indicates a radius search
            'titleReference', // indicates a title reference search
            'ownerLastName', // indicates a name search
            'companyName', // indicates a company search
            'legalDescription', // indicates a legal description search
            'valuationReference', // indicates a roll/valuation search
            'addressBuildingName', // indicates a building name search
            'addressParcelSuburbState', // indicates a parcel search
            'volumeNumber' // indicates a volume/folio search
        ].some(param => urlParams.has(param));
    };

    // Custom hook to handle direct URL filters
    useRefineSearchEventTracker({
        initialized,
        statusType: initialStatusType || props.statusType,
        filters,
        searchType: props.searchType,
        logFilters,
        shouldTrack: props.withPermaLinks,
    });

    useEffect(() => {
        const hasSuggestions = isASearchType();
        const isAddressSearch = props.searchType === ListPropertyPreview.SEARCH_TYPE.ADDRESS;
        const isMapSearch = !!location.state?.mapSearch;
        const _statusType = initialStatusType || props.statusType;
        const _filters = filters.getFlatFilters(filtersFromUrl, _statusType)
        dispatch(setSearchStatusType(_statusType));
        if (!isAddressSearch || !isMapSearch || hasSuggestions) {
            getProperties(_filters);
        }
        if (hasSuggestions || props.isSavedSearch) {
            const extracted = extractNonBaseFilters(_filters);
            dispatch(fetchAggregateFilters({ ...extracted, ...props.baseFilters }));
        }
        setInitialized(true);
        filters.save(filtersFromUrl, _statusType);
        updateFilterUserSettings(_statusType);
    }, []);

    useEffect(() => {
        // for new searches
        if (initialized) {
            dispatch(clearSearchFilters());
            dispatch(clearDrawings());
            dispatch(setSearchStatusType(initialStatusType));
            resetToDefault(initialStatusType, false, true);
            // clears location.state — CWE-80 fix: apply encodeURI (Veracode-recognized cleanser)
            let safeUrl;
            try {
                safeUrl = encodeURI(decodeURI(window.location.href));
            } catch (e) {
                safeUrl = encodeURI(window.location.href);
            }
            window.history.replaceState({ mapSearch: !!location.state?.mapSearch }, '', safeUrl);

            // Fetch aggregate filter options only when search suggestion changes
            const _filters = extractNonBaseFilters(filters.getFlatFilters(filtersFromUrl, initialStatusType));
            dispatch(fetchAggregateFilters({..._filters, ...props.baseFilters}));
        }
        // if there is no suggestions in the URL, skip the updateUrl call
        if (!!location.search) {
            updateUrl({
                statusType: initialStatusType,
                ...filters.getFlatFilters(filtersFromUrl, initialStatusType)
            });
        }
    }, [JSON.stringify(props.baseFilters)]);

    useEffect(() => {
        return () => {
            dispatch(clearDrawings());
        }
    }, []);

    const context = useMemo(() => ({
        setFilter: filters.update,
        applyFilter,
        applySort,
        resetFilter,
        filters: filters.value[statusType],
        isSearchDisabled: !filters.dirty || !!errorFields[statusType].length,
        isResetDisabled: filters.isResetDisabled,
        isDefaultValue: filters.isDefaultValue,
        isZeroValue: filters.isZeroValue,
        statusType,
        getFlatFilters: filters.getFlatFilters,
        onChangeStatusType: handleOnChangeStatusType,
        onFormError: _setErrorFields,
        errorFields: errorFields[statusType],
        isRadiusSearch: props.isRadiusSearch,
        appliedFilters: filters.previousValue[statusType],
        isModified: filters.isModified,
        searchType: props.searchType,
        defaultFilters: filters.defaultValue[statusType],
        isSavedSearch: props.isSavedSearch,
        filtersDisabled: filters.disabled,
        setFiltersDisabled: filters.setDisabled,
        isASearchType,
        resetToDefaultMap,
    }), [
        JSON.stringify(filters.value),
        JSON.stringify(filters.previousValue),
        JSON.stringify(filters.defaultValue),
        JSON.stringify(errorFields),
        JSON.stringify(props.filters),
        JSON.stringify(props.baseFilters),
        JSON.stringify(props.savedList),
        statusType,
        props.isSavedSearch,
        props.isRadiusSearch,
        filters.dirty,
        props.searchType,
        filters.disabled,
        JSON.stringify(location),
        JSON.stringify(radiusSearchTargetProperty),
        handleOnChangeStatusType,
        _setErrorFields,
    ]);
    return (
        <FiltersFormContext.Provider value={context}>
            { props.children }
        </FiltersFormContext.Provider>
    );
};

FiltersFormContextProvider.propTypes = {
    sort: PropTypes.oneOf(Object.values(RapidSearchConstant.SEARCH_DEFAULT_SORT)),
    isSavedSearch: PropTypes.bool,
    isRadiusSearch: PropTypes.bool,
    filters: PropTypes.object,
    savedList: PropTypes.shape({
        id: PropTypes.string,
        limit: PropTypes.number,
    }),
    baseFilters: PropTypes.object,
    urlFilters: PropTypes.object,
    searchType: PropTypes.string,
    withPermaLinks: PropTypes.bool,
};

FiltersFormContextProvider.defaultProps = {
    sort: RapidSearchConstant.SEARCH_DEFAULT_SORT.ADDRESS,
    statusType: OTM.ALL,
    withPermaLinks: false,
};

export default FiltersFormContextProvider;
